import { useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Settings } from 'lucide-react'
import TalentListPage from '../components/Talent/TalentListPage'
import FavoriteListsManagerModal from '../components/Favorites/FavoriteListsManagerModal'
import { Autocomplete } from '@anyemp/ui-kit'
import talentFavoritesService from '../services/talentFavoritesService'
import { users } from '../services/apiService'
import { useAuth } from '../hooks/useAuth'
import { useFavorites } from '../context/FavoritesContext'

/**
 * Страница «Избранное».
 *
 * Таблицу целиком берёт у TalentListPage — оттуда же приходят фильтры,
 * сортировки, настройка колонок и экспорт. Здесь только выбор чьё избранное
 * и какой список показывать.
 */
const Favorites = () => {
  const { user, hasPermission } = useAuth()
  const { lists: myLists } = useFavorites()
  const [searchParams] = useSearchParams()

  const canReadForeign = hasPermission('talent-service', 'talent_favorite', 'read-all')
  const myId = user?.id ? String(user.id) : ''

  // Выбор живёт в состоянии, а не в отдельных query-параметрах: TalentListPage
  // зеркалит свои фильтры в URL через setSearchParams(next), полностью заменяя
  // строку запроса ключами из URL_FILTER_KEYS. Собственные параметры страницы
  // стирались бы сразу после клика. Из URL читаем только начальные значения —
  // по тем же ключам, что пишет TalentListPage, поэтому ссылка остаётся рабочей.
  const [ownerId, setOwnerId] = useState(() => searchParams.get('favorites_of') || myId)
  const [listId, setListId] = useState(() => {
    const raw = searchParams.get('favorite_list_id')
    return raw ? raw.split(',')[0].trim() : ''
  })

  const isOwn = !ownerId || String(ownerId) === myId

  const [foreignLists, setForeignLists] = useState([])
  const [ownerOptions, setOwnerOptions] = useState([])
  const [showManager, setShowManager] = useState(false)

  const lists = isOwn ? myLists : foreignLists

  // user приезжает асинхронно: если на первом рендере его ещё нет,
  // подставляем свой id, когда он появится
  useEffect(() => {
    if (!ownerId && myId) setOwnerId(myId)
  }, [myId, ownerId])

  // В селекте только те, у кого избранное действительно есть: иначе выбор
  // большинства пользователей давал бы заведомо пустую таблицу.
  useEffect(() => {
    if (!canReadForeign) return
    let cancelled = false
    Promise.all([talentFavoritesService.owners(), users.getAllUsers()])
      .then(([ownersResponse, usersResponse]) => {
        if (cancelled) return
        const owners = ownersResponse?.data || []
        const allUsers = usersResponse?.success
          ? (Array.isArray(usersResponse.data) ? usersResponse.data : usersResponse.data?.users || [])
          : []
        const byId = new Map(allUsers.map(u => [String(u.id), u]))
        setOwnerOptions(owners.map(owner => {
          const found = byId.get(String(owner.user_id))
          return {
            id: owner.user_id,
            name: found?.name || found?.email || owner.user_id,
            items_count: owner.items_count,
          }
        }))
      })
      .catch(error => console.error('Failed to load favorite owners:', error))
    return () => { cancelled = true }
  }, [canReadForeign])

  useEffect(() => {
    if (isOwn) {
      setForeignLists([])
      return
    }
    let cancelled = false
    talentFavoritesService.listLists(ownerId)
      .then(response => { if (!cancelled) setForeignLists(response?.data || []) })
      .catch(error => {
        if (!cancelled) {
          console.error('Failed to load favorite lists:', error)
          setForeignLists([])
        }
      })
    return () => { cancelled = true }
  }, [ownerId, isOwn])

  // Выбранный список мог исчезнуть при смене владельца
  useEffect(() => {
    if (!listId || lists.length === 0) return
    if (lists.some(list => String(list.id) === String(listId))) return
    setListId('')
  }, [listId, lists])

  const handleOwnerChange = (value) => {
    setOwnerId(value || myId)
    // списки принадлежат владельцу — чужой id тут больше не годится
    setListId('')
  }

  const ownerName = useMemo(() => {
    if (isOwn) return null
    const found = ownerOptions.find(option => String(option.id) === String(ownerId))
    return found?.name || ownerId
  }, [isOwn, ownerId, ownerOptions])

  return (
    <>
      <title>Favorites | Talent Service</title>

      <TalentListPage
        talentType="all"
        pageTitle="Favorites"
        pageDescription={isOwn
          ? 'Talents you marked, grouped into your own lists'
          : `Favorites of ${ownerName}`}
        favoritesOf={ownerId || ''}
        favoriteListId={listId || null}
        defaultFilters={{
          favorites_of: ownerId || '',
          // сначала недавно отмеченные — на этой странице это ожидаемый порядок
          sort_by: 'favorited_at',
          sort_order: 'DESC',
          ...(listId ? { favorite_list_id: Number(listId) } : {})
        }}
        headerExtra={<div className="mt-4 flex min-w-0 flex-wrap items-end gap-3">
        {canReadForeign && (
          <div className="w-full min-w-0 sm:w-auto sm:min-w-[200px]">
            <Autocomplete
              clearable
              label="Favorites of"
              value={isOwn ? '' : String(ownerId)}
              onChange={(value) => handleOwnerChange(value || '')}
              placeholder="Me"
              options={[
                { value: '', label: 'Me' },
                ...ownerOptions
                  .filter(option => String(option.id) !== myId)
                  .map(option => ({
                    value: String(option.id),
                    label: `${option.name} (${option.items_count})`
                  }))
              ]}
            />
          </div>
        )}

        <div className="w-full min-w-0 sm:w-auto sm:min-w-[200px]">
          <Autocomplete
            clearable
            label="List"
            value={listId}
            onChange={(value) => setListId(value || '')}
            placeholder="All lists"
            options={[
              { value: '', label: 'All lists' },
              ...lists.map(list => ({
                value: String(list.id),
                label: `${list.name} (${list.items_count ?? 0})`
              }))
            ]}
          />
        </div>

        {isOwn && (
          <button
            type="button"
            onClick={() => setShowManager(true)}
            className="inline-flex min-h-10 items-center rounded-lg border border-light-border-primary px-3 py-1.5 text-sm font-medium text-light-text-secondary hover:text-light-text-primary dark:border-dark-border-primary dark:text-dark-text-secondary dark:hover:text-dark-text-primary"
          >
            <Settings className="w-4 h-4 mr-1.5" />
            Manage lists
          </button>
        )}

        {!isOwn && (
          <p className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary">
            The stars still show your own marks.
          </p>
        )}
        </div>}
      />

      <FavoriteListsManagerModal isOpen={showManager} onClose={() => setShowManager(false)} />
    </>
  )
}

export default Favorites
